import { useNavigate, useParams } from 'react-router-dom';

import { CareerCategory, CareerPath, AccordionDropdown } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathWithScore } from '@backrs_ui/grow-ui/src/models/models';
import {
  getCareerPathsForCategory,
  getCategoryIcon,
  getStrongFitCountForCategory,
  getCareerPathFitColor,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';

import { Box, Typography, SxProps, Theme, Divider } from '@mui/material';

import { useAgentContext } from './AllCareerPaths.agentContext';
import { FitPathsLegend } from './FitPathsLegend';

const STRINGS = appStrings.modules.careerPath.summaryPage.allCareerPaths;

type AllCareerPathsProps = Readonly<{
  careerCategories: CareerCategory[];
  careerPaths: CareerPath[];
  careerPathSuggestions: CareerPathWithScore[] | undefined;
  userHasBlueprint: boolean;
  isShared?: boolean;
  onCareerPathSelect?: (careerPath: CareerPath) => void;
}>;

export const AllCareerPaths = ({
  careerCategories,
  careerPaths,
  careerPathSuggestions,
  userHasBlueprint,
  isShared = false,
  onCareerPathSelect,
}: AllCareerPathsProps) => {
  useAgentContext(careerCategories, careerPaths);
  const navigate = useNavigate();
  const params = useParams();
  const sharedSessionUUID = params.uuid;

  const handleCareerPathClick = (careerPath: CareerPath) => {
    if (onCareerPathSelect) {
      onCareerPathSelect(careerPath);

      return;
    }

    if (isShared) {
      navigate(
        `${APP_ROUTE.SHARED_CAREER_PATHS.replace(':uuid', sharedSessionUUID || '')}/${careerPath.id}`,
      );
    } else {
      navigate(`${APP_ROUTE.CAREER_PATHS}/${careerPath.id}`);
    }
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={headerSx}>
        <Typography variant="text-lg-semibold">{STRINGS.title}</Typography>

        <Box sx={descriptionAndLegendSx}>
          <Typography
            variant="text-sm-regular"
            color={(theme: Theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
          >
            {STRINGS.description}
          </Typography>

          <FitPathsLegend />
        </Box>
      </Box>

      <Box sx={dividerSx}>
        <Divider />
      </Box>

      <Box sx={accordionContainerStyle}>
        {careerCategories.map((category) => {
          const categoryPaths = getCareerPathsForCategory(category.id, careerPaths);
          const strongFitCount = userHasBlueprint
            ? getStrongFitCountForCategory(
                category.id.toString(),
                careerPathSuggestions,
                careerCategories,
              )
            : 0;

          // Create subtitle objects with icon visibility
          const subtitleItems = categoryPaths.map((path) => ({
            text: path.name,
            showSubtitleIcon: true,
            subtitleIconColor: getCareerPathFitColor(
              careerPathSuggestions?.find((cp) => cp.careerPath.id === path.id)?.scoreSection,
            ),
          }));

          return (
            <Box key={category.id} sx={accordionWrapperStyle}>
              <AccordionDropdown
                id={`all-career-paths-accordion-${category.slug}`}
                key={category.id}
                title={category.short_name}
                iconName={getCategoryIcon(category.slug)}
                subtitles={subtitleItems}
                badgeCount={strongFitCount}
                onSubtitleClick={(subtitle) => {
                  const careerPath = categoryPaths.find((path) => path.name === subtitle);

                  if (careerPath) {
                    handleCareerPathClick(careerPath);
                  }
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  mb: backrs.spacing['spacing-2xl'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  pb: backrs.spacing['spacing-4xl'],
});

const descriptionAndLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const accordionContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  px: backrs.spacing['spacing-none'],
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'start',
  alignContent: 'start',
  width: '100%',
  '@container (min-width: 600px)': {
    gap: backrs.spacing['spacing-xl'],
  },
  '@container (min-width: 900px)': {
    gap: backrs.spacing['spacing-2xl'],
  },
  '@container (min-width: 1200px)': {
    px: backrs.spacing['spacing-3xl'],
    gap: backrs.spacing['spacing-3xl'],
  },
});

const accordionWrapperStyle: SxProps<Theme> = {
  '@container (max-width: 400px)': {
    '& .MuiAccordion-root': {
      minWidth: 'unset',
      width: '100%',
    },
    '& .MuiAccordionSummary-root': {
      padding: '12px',
      minHeight: 'unset',
      height: 'auto',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '& .MuiTypography-root': {
      fontSize: '14px',
      lineHeight: '1.2',
    },
    '& .MuiListItem-root': {
      padding: '8px 0',
    },
    '& .MuiListItemText-primary': {
      fontSize: '13px',
      lineHeight: '1.2',
    },
  },
};
